import React from 'react';

import Fields from './fields';
import i18n from 'i18';

// @mui
import {
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    AlertTitle,
    Drawer
} from '@mui/material';
import { formatError } from 'utils/formatError';
import { LoadingButton } from '@mui/lab';
import helper from '../utils/helper';
import { AppDataContext } from '../context/Context';
import { DialogSaveAdapter } from '../adapters/DialogSaveAdapter';
import ReadonlyFields from './fields/readonly-fields';
import Scrollbar from '../components/scrollbar';

export default class DialogAbstract extends React.Component {
    onClose = () => {};
    onSave = () => {};

    Name;
    Head;

    constructor(props) {
        super(props);

        this.state = {
            dialogOpen: props.dialogOpen || false,
            dialogData: props.dialogData || {},
            alertText: null,
            loading: false,
            permissions: {},
            userInfo: {}
        };

        this.Name = props.Name;
        this.Head = props.Head;
        this.Server = props.Server;

        this.onClose = props.onClose;
        this.onSave = props.onSave;

        this.wrapperRef = React.createRef();
    }

    static getDerivedStateFromProps(props, state) {
        if (props.dialogOpen !== state.dialogOpen) {
            return {
                dialogOpen: props.dialogOpen,
                dialogData: props.dialogData || {},
                alertText: null,
                loading: false
            };
        }

        return null;
    }

    static contextType = AppDataContext;

    componentDidMount() {
        this.setState({
            permissions: this.context.getState().permissions,
            userInfo: this.context.getState().userInfo
        });

        this.unsubscribe = this.context.subscribe(() => {
            this.setState({
                permissions: this.context.getState().permissions,
                userInfo: this.context.getState().userInfo
            });
        });
    }

    componentWillUnmount() {
        this.unsubscribe();
    }

    hasPermissions = () => {
        return !!Object.keys(this.state.permissions).length;
    };

    allowedPermission = (action, controller = this.Name) => {
        const adaptedController = helper.adaptedName(controller);
        const { permissions } = this.state;

        if (!this.hasPermissions()) {
            return false;
        }

        const permission = permissions?.[adaptedController]?.[action];

        if (permission) {
            return permission === 'allow_all' || permission === 'allow_own';
        }

        return false;
    };

    handleDialogCancel = (event, name) => {
        this.onClose();
    };

    handleDialogSave = async (event, name) => {
        let data = this.state.dialogData;

        const adapter = new DialogSaveAdapter(data);

        data = adapter.newData;

        this.setState({ loading: true });

        let result;

        if (data.id) {
            result = await this.Server.update(data.id, data);
        } else {
            result = await this.Server.add(data);
        }

        if (result.error === 'OK') {
            this.setState({ loading: false });
            this.onSave(this.state.dialogData);
            this.onClose();
        } else {
            this.setState({ alertText: formatError(result), loading: false });
        }
    };

    handleDialogClose = (event, name) => {
        // what to do if we have data changed and dialog is closing
        this.onClose();
    };

    handleDialogValueChanged(id, value) {
        let data = this.state.dialogData;
        data[id] = value;
        this.setState({ dialogData: data });
    }

    getValue = (field, data) => {
        return data[field.id];
    };

    render() {
        const currentRole = this.state.userInfo?.role?.systemRole;

        let data = this.state.dialogData || {};

        const isEdit = !!data.id;

        let editorFields = this.Head.filter((f) => !f.disableEditor)
            .filter((f) => (f.role ? f.role === currentRole : true))
            .filter((f) => (isEdit ? true : !f.hideOnAdd))
            .filter((f) => !f.hideInEditor);

        let permissionFilterFields = editorFields.filter((f) =>
            f.from
                ? this.allowedPermission('read', helper.adaptedName(f.from))
                : true
        );
        if (!this.hasPermissions()) {
            return null;
        }

        return (
            <>
                <Drawer
                    open={this.state.dialogOpen}
                    onClose={(event) => this.handleDialogClose(event)}
                    anchor="right"
                    ModalProps={{
                        keepMounted: true
                    }}
                    sx={{
                        "width": { sm: '100%', md: '40%', lg: '30%' },
                        '& .MuiDrawer-paper': {
                            width: { sm: '100%', md: '40%', lg: '30%' },
                            overflow: 'auto',
                            boxSizing: 'border-box'
                        }
                    }}
                >
                    <DialogTitle>
                        {isEdit
                            ? i18n.t(`pages.${this.Name}.edit`)
                            : i18n.t(`pages.${this.Name}.add`)}
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            padding: 0,
                            borderTop: '1px solid #E4E7EC',
                            borderBottom: '1px solid #E4E7EC',
                            // position: 'relative'
                        }}
                        ref={this.wrapperRef}
                    >
                        {this.state.alertText && (
                            <Alert key="alert" severity="error">
                                <AlertTitle>{i18n.t('text.error')}</AlertTitle>
                                {this.state.alertText}
                            </Alert>
                        )}

                        <Scrollbar
                            sx={{
                                padding: '20px 24px',
                                height: '100vh'
                            }}
                        >
                            {permissionFilterFields.map((field) => {
                                if (field.readOnly) {
                                    let ReadOnlyField =
                                        ReadonlyFields[field.cell];
                                    if (!ReadOnlyField)
                                        ReadOnlyField = ReadonlyFields.Text;
                                    return (
                                        <ReadOnlyField
                                            field={field}
                                            value={data}
                                            key={field.id}
                                        />
                                    );
                                }
                                const fieldValue = this.getValue(field, data);
                                let Field = Fields[field.cell];
                                if (!Field) Field = Fields.Text; // fallback to text
                                return (
                                    <Field
                                        key={field.id}
                                        field={field}
                                        value={fieldValue}
                                        data={data}
                                        context={this.context}
                                        wrapperRef={this.wrapperRef}
                                        onChange={(id, value) =>
                                            this.handleDialogValueChanged(
                                                id,
                                                value
                                            )
                                        }
                                    />
                                );
                            })}
                        </Scrollbar>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            fullWidth
                            disabled={this.state.loading}
                            color="secondary"
                            variant="outlined"
                            onClick={(event) => this.handleDialogCancel(event)}
                        >
                            {i18n.t('buttons.cancel')}
                        </Button>
                        <LoadingButton
                            fullWidth
                            color="primary"
                            variant="contained"
                            loading={this.state.loading}
                            onClick={(event) => this.handleDialogSave(event)}
                        >
                            {i18n.t('buttons.save')}
                        </LoadingButton>
                    </DialogActions>
                </Drawer>
            </>
        );
    }
}
